import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  fetchProductivityDepartmentOptions,
  fetchProductivitySessions,
  fetchProductivityUserOptions,
} from '../../api/productivityApi'

const PAGE_SIZE = 10

function _triggerCsvDownload(rows) {
  const header = ['User', 'Department', 'Date', 'Login Time', 'Logout Time', 'Duration', 'Productive', 'Idle', 'Efficiency', 'Logout Type', 'Status']
  const lines = rows.map((row) =>
    [
      row.userName,
      row.departmentName,
      row.sessionDate,
      row.loginTime,
      row.logoutTime === 'ACTIVE_NOW' ? 'Active Now' : row.logoutTime,
      row.durationLabel,
      row.productiveLabel,
      row.idleLabel,
      `${row.efficiencyPercent}%`,
      row.logoutType,
      row.status,
    ]
      .map((item) => `"${String(item || '').replaceAll('"', '""')}"`)
      .join(','),
  )
  const csvText = [header.join(','), ...lines].join('\n')
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'productivity-sessions.csv'
  link.click()
  URL.revokeObjectURL(url)
}



function _getMonthStart() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}-01`
}

function _getToday() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function useProductivitySessions() {
  const [page, setPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [userId, setUserId] = useState('all')
  const [departmentId, setDepartmentId] = useState('all')
  const [status, setStatus] = useState('all')
  const [dateFrom, setDateFrom] = useState(_getMonthStart)
  const [dateTo, setDateTo] = useState(_getToday)
  const [rows, setRows] = useState([])
  const [weeklyBars, setWeeklyBars] = useState([])
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: PAGE_SIZE, hasMore: false })
  const [userOptions, setUserOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    async function loadOptions() {
      try {
        const [users, departments] = await Promise.all([
          fetchProductivityUserOptions(),
          fetchProductivityDepartmentOptions(),
        ])
        if (!active) return
        setUserOptions(users)
        setDepartmentOptions(departments)
      } catch {
        if (!active) return
        setUserOptions([])
        setDepartmentOptions([])
      }
    }
    loadOptions()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (userId === 'all') return
    const isValid = userOptions.some((item) => String(item.id) === String(userId))
    if (!isValid) {
      setUserId('all')
      setPage(1)
    }
  }, [userId, userOptions])

  useEffect(() => {
    if (departmentId === 'all') return
    const isValid = departmentOptions.some((item) => String(item.id) === String(departmentId))
    if (!isValid) {
      setDepartmentId('all')
      setPage(1)
    }
  }, [departmentId, departmentOptions])

  useEffect(() => {
    let active = true
    async function loadSessions() {
      setIsLoading(true)
      setError('')
      try {
        const commonFilters = {
          user_id: userId === 'all' ? undefined : Number(userId),
          department_id: departmentId === 'all' ? undefined : Number(departmentId),
          status: status === 'all' ? undefined : status,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }

        const response = await fetchProductivitySessions({
          page,
          page_size: PAGE_SIZE,
          includeTotal: true,
          search: searchText.trim() || undefined,
          ...commonFilters,
        })

        if (!active) return
        setRows(Array.isArray(response?.table?.items) ? response.table.items : [])
        setWeeklyBars(Array.isArray(response?.productiveOutputMatrix?.bars) ? response.productiveOutputMatrix.bars : [])
        setMeta({
          total: Number(response?.table?.total || 0),
          page: Number(response?.table?.page || page),
          pageSize: Number(response?.table?.pageSize || PAGE_SIZE),
          hasMore: Boolean(response?.table?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setWeeklyBars([])
        setError(extractApiErrorMessage(requestError, 'Failed to load productivity sessions.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }
    loadSessions()
    return () => {
      active = false
    }
  }, [dateFrom, dateTo, departmentId, page, searchText, status, userId])

  const totalPages = useMemo(() => {
    if (!meta.total) return Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / Math.max(meta.pageSize, 1)))
  }, [meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * PAGE_SIZE + 1
  }, [page, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  function resetPageAndSet(setter, value) {
    setter(value)
    setPage(1)
  }

  const exportReport = useCallback(async () => {
    setIsExporting(true)
    try {
      const commonFilters = {
        user_id: userId === 'all' ? undefined : Number(userId),
        department_id: departmentId === 'all' ? undefined : Number(departmentId),
        status: status === 'all' ? undefined : status,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }
      const response = await fetchProductivitySessions({
        page: 1,
        page_size: 100000,
        includeTotal: false,
        search: searchText.trim() || undefined,
        ...commonFilters,
      })
      const allItems = Array.isArray(response?.table?.items) ? response.table.items : []
      _triggerCsvDownload(allItems)
    } catch {
      // silently fail export — user can retry
    } finally {
      setIsExporting(false)
    }
  }, [userId, departmentId, status, dateFrom, dateTo, searchText])

  return {
    state: {
      page,
      totalPages,
      totalCount: meta.total,
      rangeStart,
      rangeEnd,
      pageTokens: buildPaginationTokens(page, totalPages),
      rows,
      isLoading,
      isExporting,
      error,
      searchText,
      userId,
      departmentId,
      status,
      dateFrom,
      dateTo,
      userOptions,
      departmentOptions,
      weeklyBars: weeklyBars.map((item) => ({
        day: item.day,
        active: Number(item.activeHours || 0),
        idle: Number(item.idleHours || 0),
      })),
    },
    actions: {
      setSearchText: (value) => resetPageAndSet(setSearchText, value),
      setUserId: (value) => resetPageAndSet(setUserId, value),
      setDepartmentId: (value) => resetPageAndSet(setDepartmentId, value),
      setStatus: (value) => resetPageAndSet(setStatus, value),
      setDateFrom: (value) => resetPageAndSet(setDateFrom, value),
      setDateTo: (value) => resetPageAndSet(setDateTo, value),
      goToPage: (next) => {
        if (next < 1 || next > totalPages) return
        setPage(next)
      },
      goToPreviousPage: () => setPage((prev) => Math.max(1, prev - 1)),
      goToNextPage: () => setPage((prev) => Math.min(totalPages, prev + 1)),
      exportReport,
    },
  }
}
